import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSocieties } from "../../../store/slices/societySlice";
import {
  Home,
  Users,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Bell,
  User,
  Building,
  ChevronDown,
  LayoutDashboard,
  CreditCard,
  FolderSync,
  Landmark,
  Award,
  CornerDownRight,
  Map,
  Grid2x2X,
  FileText,
  UserPlus,
} from "lucide-react";
import { useSidebarContext } from "./SidebarContext";
import {
  clearPermissionsState,
  fetchUserPermissions,
} from "../../../store/slices/permissionSlice";

const getOrderedMenuItemsForRole = (role) => {
  if (role === "Customer") {
    return [
      {
        id: "user-info",
        label: "User Information",
        icon: User,
        link: "/customer/active-info",
        roles: ["Customer"],
      },
      {
        id: "payments-paid",
        label: "Installments & Plot Details",
        icon: CreditCard,
        link: "/installments-payment/paid-installments",
        roles: ["Customer"],
      },
      {
        id: "messages",
        label: "Messages",
        icon: MessageSquare,
        roles: ["Customer"],
        subMenu: [
          {
            id: "messages-dashboard",
            label: "Message Dashboard",
            link: "/messaging-dashboard",
            roles: ["Customer"],
          },
        ],
      },
      {
        id: "alerts",
        label: "Alerts",
        icon: Bell,
        roles: ["Customer"],
        subMenu: [
          {
            id: "alerts-notifications",
            label: "Notifications",
            link: "/alert/notifications",
            roles: ["Customer"],
          },
        ],
      },
      {
        id: "map",
        label: "Society Map",
        icon: Map,
        roles: ["Customer"],
        subMenu: [
          {
            id: "map-view",
            label: "Map",
            link: "/society_map/map",
            roles: ["Customer"],
          },
        ],
      },
    ];
  }

  return [
    // ─── Dashboard ───
    {
      id: "dashboard",
      label: "Dashboard",
      icon: LayoutDashboard,
      link: "/",
      roles: ["Admin", "Employee"],
    },

    // ─── Society Management ───
    {
      id: "society",
      label: "Society Management",
      icon: Building,
      roles: ["Admin"],
      subMenu: [
        {
          id: "society-creation",
          label: "Societies",
          link: "/housing-society-management/create-social-projects",
          roles: ["Admin"],
        },
        {
          id: "society-plots",
          label: "Plots Management",
          link: "/housing-society-management/plots-management",
          roles: ["Admin"],
        },
        {
          id: "society-listings",
          label: "Plots Listing",
          link: "/housing-society-management/plots-list",
          roles: ["Admin"],
        },
      ],
    },

    // ─── User Management ───
    {
      id: "users",
      label: "User Management",
      icon: Users,
      roles: ["Admin", "Employee"],
      designation: ["Manager"],
      subMenu: [
        {
          id: "users-customers",
          label: "Customers",
          link: "/user-creation/customer-creation",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "users-employees",
          label: "Employees",
          link: "/user-creation/employee-creation",
          roles: ["Admin"],
        },
        {
          id: "commission-dealers-list",
          label: "Dealers / Agents",
          link: "/commission/dealers",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },

    // ─── Sales & Bookings ───
    {
      id: "booking",
      label: "Sales & Bookings",
      icon: Home,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "booking-process",
          label: "New Booking",
          link: "/plots-booking/booking-process",
          roles: ["Employee", "Admin"],
        },
        {
          id: "booking-all",
          label: "All Bookings",
          link: "/plots-booking/all-bookings",
          roles: ["Employee", "Admin"],
        },
        {
          id: "booking-installments",
          label: "Installment Bookings",
          link: "/plots-booking/booking-installments",
          roles: ["Employee", "Admin"],
        },
        {
          id: "booking-full",
          label: "Fully Paid Bookings",
          link: "/plots-booking/booking-full",
          roles: ["Employee", "Admin"],
        },
        {
          id: "booking-hold",
          label: "Hold Bookings",
          link: "/plots-booking/hold-bookings",
          roles: ["Employee", "Admin"],
        },
      ],
    },

    // ─── Lead Generation ───
    {
      id: "leads",
      label: "Lead System",
      icon: UserPlus,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "leads-all",
          label: "Lead Dashboard",
          link: "/lead_generation",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },

        {
          id: "leads-pending",
          label: "Follow-up Queue",
          link: "/lead_generation/pending",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "leads-unassigned",
          label: "Unassigned Hub",
          link: "/lead_generation/unassigned",
          roles: ["Admin"],
        },
      ],
    },

    // ─── Payments ───
    {
      id: "payments",
      label: "Payments",
      icon: CreditCard,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "payments-installments",
          label: "Installments",
          link: "/installments-payment/manage-installments",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "payments-overdue",
          label: "Overdue Payments",
          link: "/installments-payment/overdue-payments",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },

    // ─── Agreements ───
    {
      id: "agreements",
      label: "Agreements",
      icon: FileText,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "agreements-all",
          label: "All Agreements",
          link: "/agreements/all",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "agreements-overdue",
          label: "Expired Agreements",
          link: "/agreements/overdue",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },

    // ─── Ownership Transfer ───
    {
      id: "resell",
      label: "Ownership Transfer",
      icon: FolderSync,
      roles: ["Admin", "Employee"],
      designation: ["Manager"],
      subMenu: [
        {
          id: "resell-ownership",
          label: "Resell / Transfer Plot",
          link: "/plot-transfer-resell/transfer-plot-ownership",
          roles: ["Admin"],
        },
        {
          id: "resell-list",
          label: "Resell List",
          link: "/plot-transfer-resell/plot-resale-listings",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "transfers-creation",
          label: "Transfer Creation",
          link: "/plot-transfer-resell/plot-transfer-creation",
          roles: ["Admin"],
        },
        {
          id: "transfers-list",
          label: "Transfer List",
          link: "/plot-transfer-resell/plot-transfer-page",
          roles: ["Admin"],
        },
      ],
    },

    // ─── Adjustments ───
    {
      id: "adjustments",
      label: "Adjustments",
      icon: Grid2x2X,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "cancel-plot-dashboard",
          label: "Cancel Booking",
          link: "/cancel_plot",
          roles: ["Admin"],
        },
        {
          id: "concessions-apply",
          label: "Concessions",
          link: "/concessions/apply",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "concessions-list",
          label: "Concession List",
          link: "/concessions/list",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },

    // ─── Finance ───
    {
      id: "finance",
      label: "Finance",
      icon: Landmark,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "finance-create",
          label: "Add Transaction",
          link: "/create/transaction",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "finance-income",
          label: "Income",
          link: "/income/transaction",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "finance-expense",
          label: "Expenses",
          link: "/expense/transaction",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "finance-history",
          label: "Finance History",
          link: "/income-expense/transaction",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
        {
          id: "finance-summary",
          label: "Financial Summary",
          link: "/income-expense/financial-summary",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },

    // ─── Communication ───
    {
      id: "communication",
      label: "Communication",
      icon: MessageSquare,
      roles: ["Admin", "Employee", "Customer"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "messages-dashboard",
          label: "Messages",
          link: "/messaging-dashboard",
          roles: ["Admin", "Employee", "Customer"],
        },
        {
          id: "alerts-notifications",
          label: "Alerts",
          link: "/alert/notifications",
          roles: ["Admin", "Employee", "Customer", "Investor"],
        },
      ],
    },


    // ─── Additional Items (not in user's primary list) ───
    {
      id: "employees",
      label: "Employee Management",
      icon: Users,
      roles: ["Admin", "Employee"],
      designation: ["Manager"],
      subMenu: [
        {
          id: "employees-info",
          label: "Employee Info",
          link: "/employee-management/manage-employee-info",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "employees-salaries",
          label: "Employee Salaries",
          link: "/employee-management/salary_history",
          roles: ["Admin"],
        },
        {
          id: "employees-adjustments",
          label: "Salary Adjustments",
          link: "/employee-management/salary_management",
          roles: ["Admin"],
        },
        {
          id: "employees-advance",
          label: "Advance Salary",
          link: "/employee-management/advance_salary",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
      ],
    },
    {
      id: "customers",
      label: "Customer Details",
      icon: Users,
      roles: ["Admin", "Employee"],
      designation: ["Manager"],
      subMenu: [
        {
          id: "customers-info",
          label: "Customer Info",
          link: "/customer/information",
          roles: ["Employee", "Admin"],
          designation: ["Manager"],
        },
      ],
    },
    {
      id: "investors",
      label: "Investors",
      icon: Users,
      roles: ["Admin"],
      subMenu: [
        {
          id: "investors-info",
          label: "Investor Info",
          link: "/investor-management/manage-investor-info",
          roles: ["Admin"],
        },
      ],
    },
    {
      id: "transactions",
      label: "Transactions",
      icon: CreditCard,
      roles: ["Admin", "Employee"],
      designation: ["Manager", "Sales Officer"],
      subMenu: [
        {
          id: "cash-day-book",
          label: "Cash Day Book",
          link: "/cash-day-book",
          roles: ["Admin", "Employee"],
          designation: ["Manager", "Sales Officer"],
        },
      ],
    },
    {
      id: "scholarships",
      label: "Scholarships",
      icon: Award,
      roles: ["Admin", "Employee"],
      designation: ["Manager"],
      subMenu: [
        {
          id: "scholarships-form",
          label: "Form",
          link: "/scholarship-management/form",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "scholarships-list",
          label: "List",
          link: "/scholarship-management/list",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "scholarships-shortlist",
          label: "ShortList",
          link: "/scholarship-management/shortlist",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
        {
          id: "scholarships-approved",
          label: "Approved",
          link: "/scholarship-management/Approval",
          roles: ["Admin", "Employee"],
          designation: ["Manager"],
        },
      ],
    },
    {
      id: "map",
      label: "Society Map",
      icon: Map,
      roles: ["Admin", "Employee", "Customer", "Investor"],
      subMenu: [
        {
          id: "map-view",
          label: "Map",
          link: "/society_map/map",
          roles: ["Admin", "Employee", "Customer", "Investor"],
        },
      ],
    },
  ];
};

export default function FuturisticRealEstateSidebar() {
  // Redux and Router hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeUser, isAuthenticated } = useSelector((state) => state.auth);
  const {
    activeSociety,
    collapsed,
    setCollapsed,
    mobileOpen,
    setMobileOpen,
    gradient,
    theme,
  } = useSidebarContext();

  const [activeItem, setActiveItem] = useState("dashboard");
  const [notifications, setNotifications] = useState(3);
  const [hoverStates, setHoverStates] = useState({});
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [tooltipItem, setTooltipItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [pulseEffects, setPulseEffects] = useState({});
  const { currentUserPermissions } = useSelector((state) => state.permissions);
  const { loading: permissionsLoading } = useSelector(
    (state) => state.permissions
  );
  const { societies, fetchedAt: societiesFetchedAt } = useSelector(
    (state) => state.societies
  );

  const expanded = !collapsed;

  // Get menu items based on user role
  const menuItems = getOrderedMenuItemsForRole(activeUser?.role || "Guest");

  const user = {
    name: activeUser?.username || "User",
    role: activeUser?.role || "Guest",
    avatar: activeUser?.profile_picture || "/api/placeholder/40/40",
  };

  useEffect(() => {
    if (!isAuthenticated || !activeUser) {
      setActiveItem("dashboard");
      setOpenSubMenus({});
      setNotifications(0);
      setHoverStates({});
      setPulseEffects({});
      setTooltipItem(null);

      // Clear localStorage
      localStorage.removeItem("selectedMenuItem");
      localStorage.removeItem("expandedSection");
      localStorage.removeItem("lastPath");

      dispatch(clearPermissionsState());

      return;
    }

    const currentUserId = activeUser?.id;
    const storedUserId = localStorage.getItem("currentUserId");

    if (currentUserId && storedUserId && currentUserId !== storedUserId) {
      setActiveItem("dashboard");
      setOpenSubMenus({});
      setNotifications(0);
      setHoverStates({});
      setPulseEffects({});
      setTooltipItem(null);

      localStorage.removeItem("selectedMenuItem");
      localStorage.removeItem("expandedSection");
      localStorage.removeItem("lastPath");
      dispatch(clearPermissionsState());
      if (isAuthenticated && currentUserId) {
        dispatch(fetchUserPermissions(currentUserId));
      }
    }
    if (currentUserId) {
      localStorage.setItem("currentUserId", currentUserId);
    }
  }, [isAuthenticated, activeUser?.id, navigate, dispatch]);

  useEffect(() => {
    if (isAuthenticated && activeUser?.id && !currentUserPermissions) {
      dispatch(fetchUserPermissions(activeUser.id));
    }
  }, [activeUser?.id, isAuthenticated, currentUserPermissions, dispatch]);

  useEffect(() => {
    const initializeSidebar = async () => {
      const societiesFresh =
        societiesFetchedAt && Date.now() - societiesFetchedAt < 5 * 60 * 1000;

      if (isAuthenticated && (societies.length === 0 || !societiesFresh)) {
        try {
          await dispatch(fetchSocieties());
        } catch (e) {
          console.error("Error fetching societies:", e);
        }
      }
    };

    initializeSidebar();
  }, [isAuthenticated, dispatch, societies.length, societiesFetchedAt]);

  useEffect(() => {
    const checkScreenSize = () => {
      const isMediumOrSmaller = window.innerWidth < 1024;
      setIsMobile(isMediumOrSmaller);
      if (isMediumOrSmaller) {
        setCollapsed(false);
      }
    };

    window.addEventListener("resize", checkScreenSize);
    checkScreenSize();

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, [setCollapsed]);

  useEffect(() => {
    if (!isAuthenticated || !activeUser) return;

    const currentPath = location.pathname;

    // FIX: Handle Customer default route
    if (currentPath === "/" && activeUser.role === "Customer") {
      setActiveItem("user-info");
      localStorage.setItem("selectedMenuItem", "item-user-info");
      navigate("/customer/active-info");
      return;
    }

    const activeMenuItem = findActiveItemFromPath(currentPath);

    if (activeMenuItem) {
      setActiveItem(activeMenuItem.id);
      localStorage.setItem("selectedMenuItem", `item-${activeMenuItem.id}`);
      if (activeMenuItem.parent) {
        setOpenSubMenus({ [activeMenuItem.parent]: true });
        localStorage.setItem("expandedSection", activeMenuItem.parent);
      }
    }
  }, [location.pathname, isAuthenticated, activeUser, navigate]);

  const findActiveItemFromPath = (currentPath) => {
    if (currentPath === "/" || currentPath === "") {
      return { id: "dashboard", parent: null };
    }

    for (const menuItem of menuItems) {
      // Check main menu link (exact match)
      if (menuItem.link && menuItem.link === currentPath) {
        return { id: menuItem.id, parent: null };
      }

      // Check submenu links
      if (menuItem.subMenu) {
        for (const subItem of menuItem.subMenu) {
          if (subItem.link && subItem.link === currentPath) {
            return { id: subItem.id, parent: menuItem.id };
          }
        }
      }
    }

    // If no exact match found, return null (don't force dashboard)
    return null;
  };

  const hasPermission = (menuId, subMenuId = null) => {
    // **FIX: Always fall back to role-based when permissions are null/undefined**
    if (
      !currentUserPermissions?.menuPermissions ||
      currentUserPermissions.menuPermissions.length === 0
    ) {
      // USE checkDefaultPermission instead of checkRoleBasedPermission
      return checkDefaultPermission(
        activeUser?.role || "Guest",
        activeUser?.designation,
        menuId,
        subMenuId
      );
    }

    const menuPermission = currentUserPermissions.menuPermissions.find(
      (menu) => menu.menuId === menuId
    );

    if (!menuPermission) {
      // USE checkDefaultPermission here too
      return checkDefaultPermission(
        activeUser?.role || "Guest",
        activeUser?.designation,
        menuId,
        subMenuId
      );
    }

    if (subMenuId) {
      const subMenuPermission = menuPermission.subMenuPermissions?.find(
        (subMenu) => subMenu.subMenuId === subMenuId
      );

      if (subMenuPermission) {
        return subMenuPermission.hasAccess;
      }

      // USE checkDefaultPermission here as well
      return checkDefaultPermission(
        activeUser?.role || "Guest",
        activeUser?.designation,
        menuId,
        subMenuId
      );
    }

    return menuPermission.hasAccess;
  };

  const checkRoleBasedPermission = (menuId, subMenuId = null) => {
    const userRole = activeUser?.role || "Guest";
    const userDesignation = activeUser?.designation;

    const menuItem = menuItems.find((item) => item.id === menuId);
    if (!menuItem) return false;

    // Check role access
    const hasRoleAccess = menuItem.roles
      ? menuItem.roles.includes(userRole)
      : true;

    // For designation check: if designation array exists, user must have one of those designations OR be Admin
    const hasDesignationAccess = menuItem.designation
      ? menuItem.designation.includes(userDesignation) || userRole === "Admin"
      : true;

    // For main menu items, both role and designation must pass
    const mainMenuAccess = hasRoleAccess && hasDesignationAccess;

    if (subMenuId && menuItem.subMenu) {
      const subMenuItem = menuItem.subMenu.find((sub) => sub.id === subMenuId);
      if (!subMenuItem) return false;

      const subItemRoleAccess = subMenuItem.roles
        ? subMenuItem.roles.includes(userRole)
        : true;

      // Submenu access requires main menu access AND submenu role access
      return mainMenuAccess && subItemRoleAccess;
    }

    return mainMenuAccess;
  };
  const checkDefaultPermission = (
    role,
    designation,
    menuId,
    subMenuId = null
  ) => {
    // Find the top-level menu item definition from the shared 'menuItems' configuration
    const menuItem = menuItems.find((item) => item.id === menuId);
    if (!menuItem) return false; // Menu item definition not found

    // Check if the role is allowed for the main menu item
    const roleAllowed = menuItem.roles ? menuItem.roles.includes(role) : true;

    // Check designation for main menu:
    // If designation array exists, user must have one of those designations OR be Admin
    const designationRequired =
      menuItem.designation && menuItem.designation.length > 0;
    const designationAllowed =
      !designationRequired ||
      role === "Admin" ||
      (designation && menuItem.designation.includes(designation));

    // If checking only the main menu item access
    if (!subMenuId) {
      return roleAllowed && designationAllowed;
    }

    // --- Submenu Check ---

    // Find the submenu item definition
    const subMenuItem = menuItem.subMenu?.find((sub) => sub.id === subMenuId);
    if (!subMenuItem) return false; // Submenu item definition not found

    // Determine access rules for the submenu:
    // Roles: Use submenu's specific roles if defined, otherwise inherit from parent menu item.
    const subMenuRoles = subMenuItem.roles || menuItem.roles;
    const subRoleAllowed = subMenuRoles ? subMenuRoles.includes(role) : true;

    // Designation: Use submenu's specific designation if defined, otherwise inherit from parent.
    const subMenuDesignations = subMenuItem.designation || menuItem.designation;
    const subDesignationRequired =
      subMenuDesignations && subMenuDesignations.length > 0;
    const subDesignationAllowed =
      !subDesignationRequired ||
      role === "Admin" ||
      (designation && subMenuDesignations.includes(designation));

    // Final check: User's role and designation must satisfy the submenu's requirements (or inherited ones).
    // Additionally, the user must also satisfy the parent menu's role/designation requirements.
    return (
      roleAllowed &&
      designationAllowed &&
      subRoleAllowed &&
      subDesignationAllowed
    );
  };

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter((item) => {
    // Check main menu permission
    if (!hasPermission(item.id)) {
      return false;
    }

    // Filter submenu items based on permissions
    if (item.subMenu) {
      item.subMenu = item.subMenu.filter((subItem) => {
        return hasPermission(item.id, subItem.id);
      });

      // Include item if it has accessible sub-items or direct access
      return item.subMenu.length > 0 || hasPermission(item.id);
    }

    return true;
  });

  const handleItemClick = (id, link) => {
    // For submenu items, find the parent menu whose subMenu contains this id
    let menuId = id;
    let subMenuId = null;
    const isSubMenuItem = filteredMenuItems.some(
      (item) => item.subMenu && item.subMenu.some((sub) => sub.id === id)
    );

    if (isSubMenuItem) {
      const parentMenu = filteredMenuItems.find(
        (item) => item.subMenu && item.subMenu.some((sub) => sub.id === id)
      );
      if (parentMenu) {
        menuId = parentMenu.id;
        subMenuId = id;
      }
    }

    console.log("handleItemClick called:", { id, link, menuId, subMenuId });
    const permission = hasPermission(menuId, subMenuId);
    console.log(
      "Permission check result:",
      permission,
      "for",
      menuId,
      subMenuId
    );
    if (!permission) {
      console.warn("Access denied to menu item:", id);
      return;
    }

    // Set active item immediately
    setActiveItem(id);
    localStorage.setItem("selectedMenuItem", `item-${id}`);

    // Close mobile menu if open
    if (isMobile) {
      setMobileOpen(false);
    }

    // Navigate immediately if link exists
    if (link) {
      console.log("Navigating to:", link);
      navigate(link);
      localStorage.setItem("lastPath", link);
    } else {
      console.log("No link provided to navigate.");
    }

    // Handle submenu state for main menu items only
    if (!isSubMenuItem) {
      const menuItem = filteredMenuItems.find((item) => item.id === id);
      if (!menuItem || !menuItem.subMenu || menuItem.subMenu.length === 0) {
        setOpenSubMenus({});
        localStorage.removeItem("expandedSection");
      }
    }

    // Special case for dashboard
    if (id === "dashboard") {
      setOpenSubMenus({});
      localStorage.removeItem("expandedSection");
    }
  };

  const handleMouseEnter = (id) => {
    setHoverStates((prev) => ({ ...prev, [id]: true }));
    if (!expanded) {
      setTooltipItem(id);
    }
  };

  const handleMouseLeave = (id) => {
    setHoverStates((prev) => ({ ...prev, [id]: false }));
    setTooltipItem(null);
  };

  if (isAuthenticated && !activeUser) {
    return (
      <div className="flex h-screen">
        <div className={`relative flex flex-col h-full ${gradient} w-60`}>
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className={`absolute ${theme.tailwind.bg63}`}
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 50 + 20}px`,
                  height: "1px",
                  opacity: Math.random() * 0.5 + 0.3,
                  boxShadow: theme.mui.shadowE3,
                  transform: `rotate(${Math.random() * 360}deg)`,
                  animation: `pulse ${Math.random() * 3 + 2
                    }s infinite alternate`,
                }}
              />
            ))}
          </div>
          <div className="flex items-center justify-center h-20 px-4 relative z-10">
            <div className="w-12 h-12 rounded-md relative">
              <div
                className={`absolute inset-0 ${theme.tailwind.bg1} rounded-md animate-pulse`}
              ></div>
              <div
                className={`absolute inset-2 ${theme.tailwind.bgGradient29} rounded-sm flex items-center justify-center`}
              >
                <div
                  className={`w-4 h-4 border-2 ${theme.tailwind.border31} border-t-transparent rounded-full animate-spin`}
                ></div>
              </div>
            </div>
          </div>
          <div className="space-y-3 px-4 mt-8 relative z-10">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className={`h-10 ${theme.tailwind.bgr61} rounded-lg relative overflow-hidden`}
              >
                <div
                  className="absolute inset-0 w-20 animate-shimmer"
                  style={{
                    background: theme.mui.BgLinear1,
                    transform: "translateX(-100%)",
                    animation: `shimmer ${2 + index * 0.2}s infinite`,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-sidebar="true"
      className={`flex h-screen ${isMobile && !mobileOpen ? "hidden" : "flex"}`}
    >
      <div
        className={`relative flex flex-col h-full ${gradient} backdrop-blur-lg backdrop-filter transition-all duration-500 ease-in-out z-20 ${expanded ? "w-60" : "w-20"
          }`}
        style={{
          boxShadow: theme.mui.shadowE4,
        }}
      >
        {/* Neo-glassmorphism overlay */}
        <div
          className={`absolute inset-0 ${theme.tailwind.BgBottom5} opacity-80 rounded-r-xl z-0`}
        ></div>
        {/* Abstract holographic grid background */}
        <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={`absolute ${theme.tailwind.bg1}`}
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 20 + 5}px`,
                opacity: Math.random() * 0.5 + 0.3,
                boxShadow: theme.mui.shadowE3,
                transform: `rotate(${Math.random() * 360}deg)`,
                animation: `pulse 4s infinite alternate ease-in-out, drift ${Math.random() * 30 + 20
                  }s infinite linear`,
              }}
            />
          ))}
        </div>
        {/* Glowing circuit lines */}
        <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none">
          <div
            className={`absolute w-px h-full left-1/4 top-0 ${theme.tailwind.BgBottom10}`}
          ></div>
          <div
            className={`absolute w-px h-3/4 left-3/4 top-1/4 ${theme.tailwind.BgBottom11}`}
          ></div>
          <div
            className={`absolute w-full h-px top-1/3 left-0 ${theme.tailwind.bgr53}`}
          ></div>
          <div
            className={`absolute w-full h-px top-2/3 left-0 ${theme.tailwind.bgr54}`}
          ></div>
        </div>
        {/* Edge lighting */}
        <div
          className={`absolute top-0 bottom-0 right-0 w-px ${theme.tailwind.BgBottom12}`}
        ></div>
        <div
          className={`absolute left-0 right-0 top-0 h-px ${theme.tailwind.bgr55}`}
        ></div>
        <div
          className={`absolute left-0 right-0 bottom-0 h-px ${theme.tailwind.bgr56}`}
        ></div>
        {/* Toggle Button - Only show on desktop screens */}
        {!isMobile && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCollapsed(!collapsed);
            }}
            className={`absolute -right-3 top-20 ${theme.tailwind.bgGradient21} rounded-full p-1.5 shadow-lg z-30 transform transition-all duration-300 hover:scale-110 ${theme.tailwind.shadowhover3} focus:outline-none hover:rotate-180`}
            style={{ boxShadow: theme.mui.shadowE2 }}
          >
            {expanded ? (
              <ChevronLeft className={`h-3 w-3 ${theme.tailwind.texting3}`} />
            ) : (
              <ChevronRight className={`h-3 w-3 ${theme.tailwind.texting3}`} />
            )}
          </button>
        )}
        {/* Header Section with Futuristic Logo */}
        <div className="flex flex-col items-center justify-center pt-8 pb-6 relative overflow-hidden">
          {/* Ambient glow effect */}
          <div
            className={`absolute -top-12 left-1/2 transform -translate-x-1/2 w-48 h-48 ${theme.tailwind.bg1} rounded-full blur-3xl`}
          ></div>

          {/* Society Image with holographic enhancements */}
          {expanded && activeSociety && (
            <div className="mb-4 flex flex-col items-center relative z-10">
              <div className="relative group transform transition-all duration-500 hover:scale-105">
                {/* Holographic glow ring */}
                <div
                  className={`absolute inset-0 rounded-full ${theme.tailwind.bgr52} blur-md transform scale-110 group-hover:scale-125 transition-transform`}
                ></div>

                {/* Metallic ring */}
                <div
                  className={`absolute inset-0 rounded-full border ${theme.tailwind.border16} shadow-inner`}
                ></div>

                {/* Actual image */}
                <div className="relative w-20 h-20 rounded-full p-1 overflow-hidden backdrop-blur-sm">
                  <div
                    className={`absolute inset-0 ${theme.tailwind.bgGradient27} rounded-full`}
                  ></div>
                  <img
                    src={
                      activeSociety.society_image || "/api/placeholder/60/60"
                    }
                    alt={activeSociety.name}
                    className="relative w-full h-full rounded-full object-cover z-10"
                    style={{ mixBlendMode: "normal" }}
                  />

                  {/* Reflective highlight */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-40 rounded-full z-20"></div>
                </div>
              </div>

              {/* Society location with futuristic scan line effect */}
              <div className="relative mt-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`text-lg font-medium ${theme.tailwind.text4} tracking-wide uppercase`}
                  >
                    {activeSociety?.name || "REAL ESTATE"}
                  </span>
                  <span
                    className={`text-sm font-medium ${theme.tailwind.text} tracking-wide`}
                  >
                    {activeSociety?.location || "Location"}
                  </span>
                </div>
                <div
                  className={`absolute -bottom-2 left-0 right-0 h-px ${theme.tailwind.bgr49}`}
                ></div>
                <div
                  className={`absolute -bottom-2 left-0 right-0 h-px ${theme.tailwind.bg30} w-0 animate-scan`}
                ></div>
              </div>

              {/* Animated divider */}
              <div className={`w-3/4 h-px ${theme.tailwind.bgr46} mt-4`}></div>
            </div>
          )}
          {!expanded && (
            <div className="relative group transform transition-all duration-500 hover:scale-105 mb-4">
              {/* Holographic glow ring */}
              <div
                className={`absolute inset-0 rounded-full ${theme.tailwind.bgr52} blur-md transform scale-110 group-hover:scale-125 transition-transform`}
              ></div>

              {/* Metallic ring */}
              <div
                className={`absolute inset-0 rounded-full border ${theme.tailwind.border16} shadow-inner`}
              ></div>

              {/* Actual image - smaller version */}
              <div className="relative w-12 h-12 rounded-full p-1 overflow-hidden backdrop-blur-sm">
                <div
                  className={`absolute inset-0 ${theme.tailwind.bgGradient27} rounded-full`}
                ></div>
                <img
                  src={activeSociety?.society_image || "/api/placeholder/40/40"}
                  alt={activeSociety?.name || "REAL ESTATE"}
                  className="relative w-full h-full rounded-full object-cover z-10"
                  style={{ mixBlendMode: "normal" }}
                />

                {/* Reflective highlight */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-40 rounded-full z-20"></div>
              </div>
            </div>
          )}
        </div>
        {/* Menu Items - Enhanced with Neo-glassmorphism */}
        <div className="flex-1 overflow-y-auto px-3 py-2 relative z-10 custom-scrollbar">
          <nav className="space-y-1">
            {filteredMenuItems.map((item) => {
              const isActive =
                activeItem === item.id || activeItem?.startsWith(`${item.id}-`);
              const hasSubMenu = item.subMenu && item.subMenu.length > 0;
              const isSubMenuOpen = openSubMenus[item.id];
              const IconComponent = item.icon;
              const isPulsing = pulseEffects[item.id];

              return (
                <div key={item.id} className="relative">
                  {/* Main Menu Item - Futuristic Neo-glassmorphism Style */}
                  <button
                    id={item.id}
                    data-menu-item={item.id}
                    className={`relative flex items-center w-full px-3 py-3 rounded-lg transition-all duration-300 ease-out group overflow-hidden ${isActive
                      ? `${theme.tailwind.bgr57} ${theme.tailwind.text} font-medium shadow-inner ${theme.tailwind.shadowhover4}`
                      : `${theme.tailwind.bgHover2} ${theme.tailwind.texting1} ${theme.tailwind.hover1}`
                      }`}
                    onClick={() => {
                      // For main menu items - always call handleItemClick if there's a link
                      if (item.link) {
                        handleItemClick(item.id, item.link);
                      }

                      // Handle submenu toggling separately
                      if (hasSubMenu && expanded) {
                        const isCurrentlyOpen = openSubMenus[item.id];
                        if (isCurrentlyOpen) {
                          setOpenSubMenus({});
                          localStorage.removeItem("expandedSection");
                        } else {
                          setOpenSubMenus({ [item.id]: true });
                          localStorage.setItem("expandedSection", item.id);
                        }
                      } else if (hasSubMenu && !expanded) {
                        // If collapsed, expand first
                        setCollapsed(false);
                        setTimeout(() => {
                          setOpenSubMenus({ [item.id]: true });
                          localStorage.setItem("expandedSection", item.id);
                        }, 100);
                      }

                      // If no link and no submenu, still set as active
                      if (!item.link && !hasSubMenu) {
                        setActiveItem(item.id);
                        localStorage.setItem(
                          "selectedMenuItem",
                          `item-${item.id}`
                        );
                      }
                    }}
                    onMouseEnter={() => handleMouseEnter(item.id)}
                    onMouseLeave={() => handleMouseLeave(item.id)}
                  >
                    {/* Highlight Line Effect for Active Item */}
                    {isActive && (
                      <div
                        className={`absolute left-0 top-0 bottom-0 w-0.5 ${theme.tailwind.bg40} rounded-full animate-pulse`}
                      ></div>
                    )}

                    {/* Background pulse animation on random items */}
                    {isPulsing && (
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bg25} animate-ripple rounded-lg`}
                      ></div>
                    )}

                    {/* Icon Container with Holographic Effect */}
                    <div
                      className={`relative flex-shrink-0 ${expanded ? "" : "mx-auto"
                        }`}
                    >
                      <IconComponent
                        className={`w-5 h-5 relative z-10 transition-all duration-300 ease-out ${isActive
                          ? `${theme.tailwind.text9}`
                          : `${theme.tailwind.texting2} ${theme.tailwind.groupHover7}`
                          }`}
                      />

                      {/* Reactive glow behind icon */}
                      <div
                        className={`absolute inset-0 ${theme.tailwind.bg40
                          }/0 rounded-full blur transition-all duration-300 ${isActive || hoverStates[item.id]
                            ? `${theme.tailwind.bg30}`
                            : ""
                          }`}
                        style={{
                          transform: "scale(1.5)",
                          opacity: isActive || hoverStates[item.id] ? 1 : 0,
                        }}
                      ></div>
                    </div>

                    {/* Menu Text with Notification Badge */}
                    {expanded && (
                      <div className="flex justify-between items-center w-full">
                        <span
                          className={`ml-3 relative whitespace-nowrap transition-all duration-300 ${isActive
                            ? `${theme.tailwind.text9} font-medium`
                            : `${theme.tailwind.texting1} ${theme.tailwind.groupHover3}`
                            }`}
                        >
                          {item.label}

                          {/* Energy line under text */}
                          {isActive && (
                            <span
                              className={`absolute -bottom-0.5 left-0 right-0 h-px ${theme.tailwind.bgr58}`}
                            ></span>
                          )}
                        </span>

                        {/* Notification badge or submenu indicator */}
                        <div className="flex items-center">
                          {item.id === "alerts" && notifications > 0 && (
                            <span className="relative flex h-2 w-2">
                              <span
                                className={`animate-ping absolute inline-flex h-full w-full rounded-full ${theme.tailwind.bg40} opacity-75`}
                              ></span>
                              <span
                                className={`relative inline-flex rounded-full h-2 w-2 ${theme.tailwind.bg13}`}
                              ></span>
                            </span>
                          )}

                          {hasSubMenu && (
                            <ChevronDown
                              className={`w-4 h-4 ml-1.5 ${theme.tailwind.texting2
                                } transform transition-transform duration-300 ${isSubMenuOpen ? "rotate-180" : ""
                                }`}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tooltip for collapsed state */}
                    {!expanded && tooltipItem === item.id && (
                      <div
                        className={`fixed ml-16 mt-5 items-center ${theme.tailwind.bg39} backdrop-blur-sm text-white text-xs whitespace-nowrap py-2 px-3 rounded-md z-50 shadow-lg ${theme.tailwind.shadowhover10} border ${theme.tailwind.border15}`}
                        style={{
                          left: "1rem",
                          top: `${document
                            .getElementById(item.id)
                            ?.getBoundingClientRect().top || 0
                            }px`,
                          transform: "translateY(-50%)",
                        }}
                      >
                        <div
                          className={`absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 ${theme.tailwind.bg14} rotate-45 border-l border-b ${theme.tailwind.border15}`}
                        ></div>
                        <div className="relative z-10 flex items-center">
                          <span>{item.label}</span>
                          {item.id === "alerts" && notifications > 0 && (
                            <span
                              className={`ml-2 ${theme.tailwind.bg13} ${theme.tailwind.texting3} text-xs rounded-full w-4 h-4 flex items-center justify-center`}
                            >
                              {notifications}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Interactive highlight panel */}
                    <div
                      className={`absolute inset-0 ${theme.tailwind.bgr59
                        } rounded-lg transition-opacity duration-300 ${hoverStates[item.id] && !isActive
                          ? "opacity-100"
                          : "opacity-0"
                        }`}
                    ></div>
                  </button>

                  {/* Sub Menu Items with Nested Animation */}
                  {hasSubMenu && isSubMenuOpen && expanded && (
                    <div
                      className={`pl-4 mt-1 transition-all duration-300 space-y-1 overflow-hidden ${isSubMenuOpen
                        ? "max-h-96 opacity-100"
                        : "max-h-0 opacity-0 pointer-events-none"
                        }`}
                    >
                      {item.subMenu.map((subItem) => {
                        const isSubActive = activeItem === subItem.id;

                        return (
                          <button
                            key={subItem.id}
                            data-menu-item={subItem.id}
                            className={`relative flex items-center w-full pr-3 pl-5 py-2 rounded-md group transition-all duration-300 ${isSubActive
                              ? `${theme.tailwind.bg25} ${theme.tailwind.text9}`
                              : `${theme.tailwind.texting2} ${theme.tailwind.hover2} ${theme.tailwind.bgHover2}`
                              }`}
                            onClick={() => {
                              console.log(
                                "Submenu clicked:",
                                subItem.id,
                                subItem.link
                              );
                              if (subItem.link) {
                                console.log(
                                  "Calling handleItemClick with:",
                                  subItem.id,
                                  subItem.link
                                );
                                handleItemClick(subItem.id, subItem.link);
                              } else {
                                console.log(
                                  "No link found for submenu:",
                                  subItem.id
                                );
                              }
                            }}
                          >
                            {/* Connector Line */}
                            <div
                              className={`absolute left-0 top-0 bottom-0 w-px ${theme.tailwind.bg1}`}
                            ></div>

                            {/* Connection branch */}
                            <div
                              className={`absolute left-0 top-1/2 w-3 h-px ${theme.tailwind.bg1}`}
                            ></div>

                            {/* Animated active indicator */}
                            {isSubActive && (
                              <div
                                className={`absolute left-5 w-1 h-1 rounded-full ${theme.tailwind.bg40}`}
                              ></div>
                            )}

                            {/* Text with subtle hover effect */}
                            <div className="ml-3 text-sm transition-all duration-300">
                              {subItem.label}

                              {/* Hover scan line */}
                              <div
                                className={`absolute bottom-0 left-5 right-3 h-px ${theme.tailwind.bg30
                                  } transform scale-x-0 origin-left transition-transform duration-300 ${isSubActive
                                    ? "scale-x-100"
                                    : "group-hover:scale-x-100"
                                  }`}
                              ></div>
                            </div>

                            {/* Subtle right edge content icon */}
                            {isSubActive && (
                              <CornerDownRight
                                className={`ml-auto w-3 h-3 ${theme.tailwind.text12}`}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
        <style
          dangerouslySetInnerHTML={{
            __html: `
    .custom-scrollbar::-webkit-scrollbar {
      width: 4px;
    }

    .custom-scrollbar::-webkit-scrollbar-track {
      background: rgba(15, 23, 42, 0.1);
      border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: ${theme.mui.bgCB};
      border-radius: 10px;
    }

    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: ${theme.mui.bgCB1};
    }
  `,
          }}
        />

        {/* User Profile Section - Futuristic Metallic Style */}
        <div className="relative mt-auto p-3">
          {/* Separator Line with Animation */}
          <div className="relative h-px w-full mb-3 overflow-hidden">
            <div className={`absolute inset-0 ${theme.tailwind.bgr49}`}></div>
            <div
              className={`absolute inset-0 ${theme.tailwind.bgr53} w-1/3 animate-bounce-x`}
            ></div>
          </div>

          <div
            className={`flex items-center p-2 rounded-xl ${theme.tailwind.bgGradient28
              } backdrop-blur-sm border ${theme.tailwind.border3} ${expanded ? "" : "justify-center"
              }`}
          >
            {/* User Avatar with Holographic Effect */}
            <div className="relative flex-shrink-0 group">
              <div
                className={`absolute inset-0 ${theme.tailwind.bgr60} rounded-full blur transform scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              ></div>
              <div
                className={`relative w-9 h-9 rounded-full overflow-hidden border ${theme.tailwind.border2}`}
              >
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />

                {/* Scan line effect */}
                <div
                  className={`absolute inset-0 ${theme.tailwind.BgBottom11} h-1/4 transform -translate-y-full animate-scan-slow`}
                ></div>
              </div>
            </div>

            {/* User Info and Status Indicator */}
            {expanded && (
              <div className="ml-3 flex-1 overflow-hidden">
                <div className="font-medium text-white truncate text-sm">
                  {user.name}
                </div>
                <div
                  className={`text-xs ${theme.tailwind.text11} flex items-center`}
                >
                  <span className="relative flex mr-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${theme.tailwind.bg40}`}
                    ></span>
                    <span
                      className={`absolute inset-0 rounded-full ${theme.tailwind.bg40} animate-ping opacity-50`}
                    ></span>
                  </span>
                  {user.role}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
